import { LitElement, html, css, unsafeCSS } from 'lit';
import throttle from 'lodash/throttle';
import styles from './tabNavigation.scss?inline';
import { isLaptopMaxWidth } from '@/helpers';
import { ref, createRef } from 'lit/directives/ref.js';

export default class SotbitTabNavigation extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        open: { type: Boolean, reflect: true },
        hasNavTopSlot: { type: Boolean, state: true },
        _isShowNavigation: { type: Boolean, state: true },
        _countNavButtons: { type: Number, state: true },
    };

    constructor() {
        super();

        this.contentRef = createRef();
        this.dragHandlerRef = createRef();
        this.tabNavigationRef = createRef();

        this.hasNavTopSlot = false;

        this.threshold = 0.7;

        this.isDragging = false;
        this.startY = 0;

        this.handleResize = this._handleResize.bind(this);
        this._isShowNavigation = isLaptopMaxWidth();
        this._countNavButtons = 0;

        this._resizeObserver = new ResizeObserver(this.handleResize);
    }

    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('resize', this.handleResize);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('resize', this.handleResize);
    }

    async updated(_changedProperties) {
        super.updated(_changedProperties);
        await this._setDataToWindow();
    }

    attributeChangedCallback(name, old, value) {
        super.attributeChangedCallback(name, old, value);

        if (name === 'open' && old !== value) {
            this._toggleView();
        }
    }

    get _btnMore() {
        const slot = this.shadowRoot.querySelector('slot[name="nav-btn-more"]');
        return slot?.assignedElements({ flatten: true })?.[0];
    }

    async _setDataToWindow() {
        await new Promise((resolve) => setTimeout(resolve));
        const rect = this.getBoundingClientRect();
        document.documentElement.style.setProperty('--tab-nav-height', rect?.height ? `${rect.height}px` : '0px');
    }

    _handleResize = throttle(async () => {
        const isLaptopMaxW = isLaptopMaxWidth();
        if (this._isShowNavigation === isLaptopMaxW && !this.hasNavTopSlot) {
            return;
        }

        this._isShowNavigation = isLaptopMaxW;

        await this._setDataToWindow();
    }, 200);

    _startDrag(e) {
        this.isDragging = true;
        this.startY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;
    }

    _drag(e) {
        if (!this.isDragging) return;

        e.preventDefault();
        const clientY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;
        const deltaY = clientY - this.startY;

        this.rect = this.contentRef.value?.getBoundingClientRect();
        this.top = this.rect.height;

        this.top = this.rect.height - deltaY;

        if (this.rect.height - deltaY < 0) {
            this.top = 0;
        }

        if (this.rect.height - deltaY >= this.rect.height) {
            this.top = this.rect.height;
        }

        this._setTopStyle(-this.top);
    }

    _endDrag() {
        if (!this.isDragging) return;

        this.isDragging = false;

        if (this.top < this.rect.height * this.threshold) {
            this._hide();
        } else {
            this._show();
        }
    }

    _handleSlotChange() {
        this._calcNavButtons();
    }

    _calcNavButtons() {
        let count = 0;
        const slots = this.shadowRoot.querySelectorAll('slot[name^="nav-btn"]');
        slots.forEach((slot) => {
            count += slot?.assignedElements({ flatten: true }).length || 0;
        });
        this._countNavButtons = count || 0;
    }

    _handleNavSlotChange() {
        this._calcNavButtons();
    }

    async _toggleView() {
        await new Promise((resolve) => setTimeout(resolve));
        this.rect = this.contentRef.value?.getBoundingClientRect();
        if (this.open) {
            this._show();
        } else {
            this._hide();
        }
    }

    _show() {
        this._setTopStyle(-this.rect.height);
        this._btnMore?.setAttribute('open', '');
        this.open = true;
    }

    _hide() {
        this._setTopStyle(0);
        this._btnMore?.removeAttribute('open');
        this.open = false;
    }

    _setTopStyle(value) {
        if (!this.contentRef.value) {
            return;
        }
        this.contentRef.value.style.top = `${value}px`;
    }

    _clickHandlerMoreBtn() {
        if (this.open) {
            this.open = false;
        } else {
            this.open = true;
        }
    }

    _clickHandler(e) {
        this.dispatchEvent(new CustomEvent('navBtnClick', e));
    }

    _handleNavTopSlotChange({ target }) {
        this.hasNavTopSlot = target?.assignedElements({ flatten: true }).length > 0;
    }

    render() {
        return this._isShowNavigation
            ? html`
                  <slot @slotchange="${this._handleNavTopSlotChange}" name="nav-top"></slot>
                  <div
                      class="tab-navigation${this.hasNavTopSlot ? ' tab-navigation__has-top' : ''}"
                      ${ref(this.tabNavigationRef)}
                  >
                      <sotbit-overlay ?show="${this.open}" fixed></sotbit-overlay>
                      <div class="tab-navigation__modal " part="nav-item-content" ${ref(this.contentRef)}>
                          <div
                              class="tab-navigation__drag-container"
                              part="nav-handler-container"
                              @touchstart="${this._startDrag}"
                              @touchmove="${this._drag}"
                              @touchend="${this._endDrag}"
                              ${ref(this.dragHandlerRef)}
                          >
                              <span class="tab-navigation__drag-handler" part="nav-handler-container"></span>
                          </div>
                          <div class="tab-navigation__content">
                              <slot name="nav-item-more" class="tab-navigation__item" part="nav-item"></slot>
                          </div>
                      </div>
                      <div
                          class="tab-navigation__nav ${this.open ? 'open' : ''}"
                          style="grid-template-columns: repeat(${this._countNavButtons}, 1fr)"
                      >
                          <slot
                              name="nav-btn"
                              part="nav-btn"
                              class="tab-navigation__item"
                              @click="${this._clickHandler}"
                              @slotchange="${this._handleNavSlotChange}"
                          ></slot>
                          <slot
                              name="nav-btn-more"
                              part="nav-btn-more"
                              @click="${this._clickHandlerMoreBtn}"
                              @slotchange="${this._handleSlotChange}"
                          ></slot>
                      </div>
                  </div>
              `
            : html``;
    }
}
